/**
 * Guidance library for "Consultoría en una Caja"
 * Each entry is matched against the normalized section name.
 * Keys are substring patterns (lowercased, no accents).
 *
 * Each entry has:
 *  - icon     : emoji for visual identity
 *  - color    : tailwind gradient classes
 *  - what     : 1-sentence description
 *  - goal     : what the output should be
 *  - steps    : 3-4 actionable instructions
 *  - questions: 3-5 key questions the user should answer
 *  - tip      : one practical advice
 *  - tasks    : pre-built tasks to auto-create on first visit (max 6)
 */

export const SECTION_GUIDES = [

  // ── Entendimiento del Modelo de Negocio ─────────────────────────────────
  {
    match: s => s.includes('contexto') && (s.includes('entorno') || s.includes('negocio')),
    icon: '🌐', color: 'from-slate-600 to-slate-700',
    what: 'Describe el contexto en el que opera tu empresa y el entorno macroeconómico relevante.',
    goal: 'Un mapa claro de los factores externos que afectan tu negocio hoy.',
    steps: [
      'Describe en 2-3 párrafos a qué se dedica tu empresa y cómo genera valor.',
      'Identifica los 3 factores del entorno que más impactan tu operación (económicos, regulatorios, tecnológicos…)',
      'Señala si hay tendencias de largo plazo que podrían cambiar las reglas del juego.',
    ],
    questions: [
      '¿En qué negocio estás realmente y cómo ganas dinero?',
      '¿Qué cambios externos han impactado más tu empresa en los últimos 2 años?',
      '¿Hay factores del entorno que hoy representan una amenaza directa?',
      '¿Qué tendencia global podría cambiar tu industria en los próximos 5 años?',
    ],
    tip: 'Sé honesto sobre el contexto real, no el ideal. Las mejores estrategias parten de un diagnóstico sin filtros.',
    tasks: [
      { name: 'Describir el negocio actual en 200 palabras (qué hacemos y cómo generamos valor)', priority: 'high' },
      { name: 'Listar los 3 factores del entorno que más nos afectan hoy', priority: 'high' },
      { name: 'Identificar 2 tendencias de largo plazo relevantes para nuestra industria', priority: 'medium' },
    ],
  },

  {
    match: s => s.includes('propuesta') && s.includes('valor'),
    icon: '💎', color: 'from-violet-600 to-violet-700',
    what: 'Define qué te hace diferente y por qué tus clientes te eligen sobre la competencia.',
    goal: 'Una propuesta de valor clara y diferenciada que puedas comunicar en 30 segundos.',
    steps: [
      'Lista los problemas o necesidades que resuelves para tus clientes.',
      'Describe qué te diferencia de otras opciones disponibles en el mercado.',
      'Formula tu propuesta de valor en 1-2 oraciones claras.',
    ],
    questions: [
      '¿Por qué un cliente debería comprarte a ti y no a la competencia?',
      '¿Qué problema específico resuelves mejor que nadie?',
      '¿Tu propuesta de valor es igual para todos tus segmentos o varía?',
      '¿Cuándo fue la última vez que validaste tu propuesta de valor con clientes reales?',
    ],
    tip: 'La propuesta de valor más poderosa no habla de características del producto — habla del resultado que obtiene el cliente.',
    tasks: [
      { name: 'Listar los 5 beneficios principales que ofrecemos a nuestros clientes', priority: 'high' },
      { name: 'Identificar los 3 diferenciadores reales frente a la competencia', priority: 'high' },
      { name: 'Redactar la propuesta de valor en máximo 2 oraciones', priority: 'medium' },
    ],
  },

  {
    match: s => s.includes('segmento') && s.includes('canal'),
    icon: '🎯', color: 'from-cyan-600 to-cyan-700',
    what: 'Identifica a quién le vendes (segmentos) y cómo llegas a ellos (canales).',
    goal: 'Un mapa de segmentos prioritarios con sus canales de venta/distribución asociados.',
    steps: [
      'Describe cada grupo de clientes con sus características clave (tamaño, necesidades, comportamiento).',
      'Indica qué canal usas para llegar a cada segmento (directo, distribuidor, digital, etc.).',
      'Califica cada segmento por rentabilidad y potencial de crecimiento.',
    ],
    questions: [
      '¿Cuántos segmentos de clientes diferentes tienes y qué los diferencia?',
      '¿Qué canales de venta usas para llegar a cada segmento?',
      '¿Cuál es tu segmento más rentable hoy? ¿Y el de mayor potencial?',
      '¿Hay segmentos que estás atendiendo mal o que podrías no estar atendiendo?',
    ],
    tip: 'No todos los clientes valen igual. Identifica el 20% de clientes que generan el 80% de tus ingresos.',
    tasks: [
      { name: 'Mapear todos los segmentos de clientes actuales con sus características', priority: 'high' },
      { name: 'Describir los canales de venta y distribución por segmento', priority: 'high' },
      { name: 'Calificar cada segmento por rentabilidad actual y potencial de crecimiento', priority: 'medium' },
    ],
  },

  {
    match: s => s.includes('fuente') && (s.includes('ingreso') || s.includes('costo')),
    icon: '💰', color: 'from-emerald-600 to-emerald-700',
    what: 'Documenta cómo genera dinero tu empresa y cuál es tu estructura de costos.',
    goal: 'Un mapa claro de dónde vienen tus ingresos y qué pesa más en tus costos.',
    steps: [
      'Lista todas las fuentes de ingreso y el porcentaje que representa cada una.',
      'Identifica tus costos fijos y variables más relevantes.',
      'Calcula o estima tu margen bruto por línea de negocio.',
    ],
    questions: [
      '¿Cuántas fuentes de ingreso tienes y qué tan diversificadas están?',
      '¿Cuál es tu costo más grande y tienes control sobre él?',
      '¿Cuáles son tus márgenes por línea de negocio o producto?',
      '¿Hay fuentes de ingreso que estás dejando de aprovechar?',
    ],
    tip: 'Muchas empresas medianas no conocen sus márgenes por línea de negocio. Calcularlo puede cambiar completamente la estrategia.',
    tasks: [
      { name: 'Listar todas las fuentes de ingreso con su participación % en ventas totales', priority: 'high' },
      { name: 'Documentar los 5 costos más relevantes (fijos y variables)', priority: 'high' },
      { name: 'Estimar margen bruto por línea de negocio o producto principal', priority: 'medium' },
    ],
  },

  {
    match: s => s.includes('capacidad') && s.includes('recurso'),
    icon: '⚙️', color: 'from-orange-600 to-orange-700',
    what: 'Identifica los recursos y capacidades clave que hacen posible tu modelo de negocio.',
    goal: 'Un inventario de tus activos estratégicos: físicos, humanos, tecnológicos e intangibles.',
    steps: [
      'Lista tus activos más valiosos: marca, tecnología, planta, talento, relaciones.',
      'Identifica qué capacidades son difíciles de replicar por la competencia.',
      'Señala las brechas: ¿qué capacidades necesitas y no tienes hoy?',
    ],
    questions: [
      '¿Qué recurso o capacidad, si lo perdieras mañana, destruiría tu negocio?',
      '¿Qué sabes hacer mejor que casi cualquier competidor?',
      '¿Qué capacidades necesitas desarrollar para ejecutar tu estrategia?',
    ],
    tip: 'Las capacidades más valiosas no siempre son las más visibles. El conocimiento acumulado del equipo suele ser el activo más difícil de copiar.',
    tasks: [
      { name: 'Inventariar los recursos clave: físicos, humanos, tecnológicos e intangibles', priority: 'high' },
      { name: 'Identificar las 3 capacidades que nos diferencian de la competencia', priority: 'high' },
      { name: 'Listar las brechas de capacidad más críticas para el futuro', priority: 'medium' },
    ],
  },

  // ── Diagnóstico Estratégico ──────────────────────────────────────────────
  {
    match: s => s.includes('pestel') || s.includes('externo'),
    icon: '🌍', color: 'from-blue-600 to-blue-700',
    what: 'Analiza los factores macro del entorno que pueden afectar tu negocio.',
    goal: 'Un mapa de las fuerzas externas (P-E-S-T-E-L) que condicionan tu estrategia.',
    steps: [
      'Para cada dimensión (Político, Económico, Social, Tecnológico, Ecológico, Legal), identifica 2-3 factores relevantes.',
      'Clasifica cada factor como oportunidad o amenaza para tu negocio.',
      'Prioriza los 5 factores de mayor impacto.',
    ],
    questions: [
      '¿Qué cambios regulatorios o políticos podrían afectar tu operación en los próximos 2 años?',
      '¿Cómo está la economía en tu sector y qué proyecta?',
      '¿Qué cambios tecnológicos podrían disruptar tu modelo de negocio?',
      '¿Hay tendencias sociales o demográficas que cambien la demanda de tus productos?',
      '¿Qué factores legales o ambientales te generan riesgo hoy?',
    ],
    tip: 'El PESTEL no es una lista de hechos generales — enfócalo en lo que es relevante para TU industria y TU empresa.',
    tasks: [
      { name: 'Identificar 2-3 factores Políticos relevantes (oportunidad o amenaza)', priority: 'high' },
      { name: 'Identificar 2-3 factores Económicos relevantes', priority: 'high' },
      { name: 'Identificar factores Sociales y Tecnológicos relevantes', priority: 'high' },
      { name: 'Identificar factores Ecológicos y Legales relevantes', priority: 'medium' },
      { name: 'Priorizar los 5 factores de mayor impacto con calificación de severidad', priority: 'medium' },
    ],
  },

  {
    match: s => s.includes('porter') || (s.includes('industria') && s.includes('analisis')),
    icon: '🏭', color: 'from-indigo-600 to-indigo-700',
    what: 'Evalúa la intensidad competitiva de tu industria usando el modelo de las 5 fuerzas de Porter.',
    goal: 'Un diagnóstico del atractivo de tu industria y los factores que más presionan tu rentabilidad.',
    steps: [
      'Evalúa cada una de las 5 fuerzas: rivalidad, nuevos entrantes, sustitutos, poder de compradores, poder de proveedores.',
      'Califica cada fuerza como alta, media o baja.',
      'Concluye: ¿es una industria atractiva? ¿Qué fuerzas debes gestionar prioritariamente?',
    ],
    questions: [
      '¿Qué tan intensa es la competencia en tu sector? ¿Hay guerra de precios?',
      '¿Qué tan fácil es para un nuevo jugador entrar a competir en tu industria?',
      '¿Tienen tus clientes mucho poder de negociación frente a ti?',
      '¿Tus proveedores pueden subir precios fácilmente o cambiar condiciones?',
      '¿Hay productos sustitutos que podrían reemplazar lo que ofreces?',
    ],
    tip: 'La industria con las 5 fuerzas más débiles es la más rentable. Entiende cuál fuerza te quita más margen hoy.',
    tasks: [
      { name: 'Evaluar Rivalidad Competitiva: intensidad, número de competidores, diferenciación', priority: 'high' },
      { name: 'Evaluar Amenaza de Nuevos Entrantes: barreras de entrada, economías de escala', priority: 'high' },
      { name: 'Evaluar Poder de Compradores y Proveedores', priority: 'high' },
      { name: 'Evaluar Amenaza de Sustitutos', priority: 'medium' },
      { name: 'Conclusión general: atractivo de la industria y fuerza prioritaria a gestionar', priority: 'medium' },
    ],
  },

  {
    match: s => s.includes('cadena') && s.includes('valor') || (s.includes('interno') && !s.includes('corp')),
    icon: '🔬', color: 'from-teal-600 to-teal-700',
    what: 'Analiza las actividades internas de tu empresa para identificar fortalezas y debilidades.',
    goal: 'Un mapa de qué haces bien, qué haces mal y dónde está tu ventaja competitiva interna.',
    steps: [
      'Recorre las actividades primarias: operaciones, marketing, ventas, servicio.',
      'Recorre las actividades de soporte: infraestructura, RRHH, tecnología, compras.',
      'Para cada área, califica si es fortaleza, neutral o debilidad.',
    ],
    questions: [
      '¿En qué proceso interno somos significativamente mejores que la competencia?',
      '¿Cuál es nuestra debilidad operativa más grande hoy?',
      '¿Dónde pierde valor la cadena? ¿Dónde hay ineficiencias costosas?',
      '¿Qué actividades internas generan la mayor ventaja competitiva?',
    ],
    tip: 'Habla con los líderes de cada área antes de completar este análisis. Ellos saben dónde están los cuellos de botella.',
    tasks: [
      { name: 'Evaluar actividades primarias: operaciones, logística, marketing y ventas', priority: 'high' },
      { name: 'Evaluar actividades de soporte: tecnología, RRHH, infraestructura', priority: 'high' },
      { name: 'Identificar las 3 fortalezas internas más relevantes', priority: 'medium' },
      { name: 'Identificar las 3 debilidades internas más críticas', priority: 'medium' },
    ],
  },

  {
    match: s => s.includes('dofa') || s.includes('foda'),
    icon: '⚡', color: 'from-amber-600 to-amber-700',
    what: 'Construye el análisis DOFA cruzando lo interno (fortalezas y debilidades) con lo externo (oportunidades y amenazas).',
    goal: 'Una matriz DOFA completa con las estrategias que surgen de cada cruce.',
    steps: [
      'Resume las fortalezas y debilidades clave del análisis interno.',
      'Resume las oportunidades y amenazas del PESTEL y el análisis de industria.',
      'Cruza cada cuadrante: FO (cómo usar fortalezas para aprovechar oportunidades), DA (cómo mitigar debilidades ante amenazas), etc.',
    ],
    questions: [
      '¿Cuáles son tus 3 mayores fortalezas internas?',
      '¿Cuáles son tus 3 debilidades más críticas?',
      '¿Qué oportunidades del entorno puedes aprovechar con tus fortalezas actuales?',
      '¿Qué amenaza externa combinada con una debilidad interna representa el mayor riesgo?',
    ],
    tip: 'El DOFA más valioso no es la lista — es el cruce. Estrategias FO (ofensivas), DA (defensivas), FA y DO son las que guían las decisiones.',
    tasks: [
      { name: 'Listar las 5 Fortalezas principales del análisis interno', priority: 'high' },
      { name: 'Listar las 5 Debilidades más relevantes', priority: 'high' },
      { name: 'Listar las 5 Oportunidades del entorno externo', priority: 'high' },
      { name: 'Listar las 5 Amenazas más significativas', priority: 'high' },
      { name: 'Construir estrategias cruzadas: FO, FA, DO, DA', priority: 'medium' },
    ],
  },

  // ── Definición Estratégica ───────────────────────────────────────────────
  {
    match: s => s.includes('mision') || s.includes('vision') || s.includes('valores'),
    icon: '🧭', color: 'from-violet-600 to-violet-700',
    what: 'Define (o redefine) el propósito, la aspiración y los principios que guían tu empresa.',
    goal: 'Una misión, visión y valores auténticos que el equipo pueda recordar y vivir.',
    steps: [
      'Redacta la misión: ¿para qué existe tu empresa? (presente)',
      'Redacta la visión: ¿dónde quieres estar en 5-10 años? (futuro inspirador)',
      'Define 3-5 valores que realmente guíen el comportamiento del equipo.',
    ],
    questions: [
      '¿Por qué existe tu empresa más allá de generar utilidades?',
      '¿Cómo quieres que sea tu empresa en 5 años? ¿Qué la hace diferente?',
      '¿Qué comportamientos reconoces y premias en tu equipo? Esos son tus valores reales.',
    ],
    tip: 'Si tu misión y visión no las puede recordar un empleado de nivel medio, hay que reescribirlas. La claridad es la meta.',
    tasks: [
      { name: 'Redactar la Misión: propósito presente de la empresa (máx. 2 oraciones)', priority: 'high' },
      { name: 'Redactar la Visión: aspiración a 5 años (inspiradora y alcanzable)', priority: 'high' },
      { name: 'Definir 3-5 Valores con su significado práctico para el equipo', priority: 'medium' },
      { name: 'Validar misión y visión con líderes clave de la organización', priority: 'medium' },
    ],
  },

  {
    match: s => s.includes('objetivo') && s.includes('estrategico'),
    icon: '🎯', color: 'from-blue-600 to-blue-700',
    what: 'Define los objetivos estratégicos que guiarán tu empresa en los próximos 3 años.',
    goal: 'Un conjunto de 4-6 objetivos estratégicos medibles, alineados con tu visión.',
    steps: [
      'Define objetivos en las 4 perspectivas: Financiero, Clientes, Procesos, Aprendizaje.',
      'Asegúrate de que cada objetivo sea específico y medible (tiene un KPI asociado).',
      'Verifica que los objetivos sean coherentes entre sí y con la visión.',
    ],
    questions: [
      '¿Cuál es el objetivo financiero más importante a 3 años (ingresos, margen, EBITDA)?',
      '¿Qué quieres lograr en términos de mercado y clientes?',
      '¿Qué procesos internos debes mejorar para alcanzar los objetivos?',
      '¿Qué capacidades o talento necesitas desarrollar?',
    ],
    tip: 'Menos es más. 4-6 objetivos bien definidos y medidos valen más que 20 objetivos vagos.',
    tasks: [
      { name: 'Definir 1-2 objetivos estratégicos Financieros con KPI y meta a 3 años', priority: 'high' },
      { name: 'Definir 1-2 objetivos de Clientes/Mercado con KPI', priority: 'high' },
      { name: 'Definir 1-2 objetivos de Procesos Internos críticos', priority: 'medium' },
      { name: 'Definir 1 objetivo de Aprendizaje/Capacidades', priority: 'medium' },
    ],
  },

  // ── Análisis del Mercado ─────────────────────────────────────────────────
  {
    match: s => s.includes('desempeno') && s.includes('mercado'),
    icon: '📊', color: 'from-cyan-600 to-cyan-700',
    what: 'Mide el tamaño, crecimiento y comportamiento del mercado en el que compites.',
    goal: 'Una visión cuantificada del mercado: tamaño actual, tendencia y factores de crecimiento.',
    steps: [
      'Busca o estima el tamaño del mercado en ventas (USD o moneda local).',
      'Calcula el crecimiento de los últimos 3-5 años (CAGR).',
      'Identifica si el mercado está creciendo, maduro o en declive.',
    ],
    questions: [
      '¿Cuánto vale en ventas el mercado total donde compites?',
      '¿A qué tasa está creciendo el mercado anualmente?',
      '¿Hay estacionalidad marcada en la demanda? ¿Cuándo son los picos?',
      '¿Qué factores están impulsando o frenando el crecimiento del mercado?',
    ],
    tip: 'Si no tienes datos exactos, una estimación razonada es mejor que ninguna. Usa fuentes como gremios, cámaras de comercio o reportes de consultoras.',
    tasks: [
      { name: 'Investigar y documentar el tamaño del mercado total (TAM) en USD', priority: 'high' },
      { name: 'Calcular la tasa de crecimiento de los últimos 3-5 años', priority: 'high' },
      { name: 'Identificar factores que están impulsando o frenando el crecimiento', priority: 'medium' },
      { name: 'Documentar estacionalidad o ciclos relevantes del mercado', priority: 'low' },
    ],
  },

  {
    match: s => (s.includes('solucion') || s.includes('producto')) && !s.includes('precio'),
    icon: '📦', color: 'from-violet-600 to-violet-700',
    what: 'Analiza el portafolio de productos/soluciones del mercado y cómo se desempeñan.',
    goal: 'Un mapa del portafolio de categorías del mercado con sus tamaños y tendencias.',
    steps: [
      'Identifica las categorías o subcategorías de productos que existen en el mercado.',
      'Estima el tamaño de cada categoría en ventas.',
      'Analiza cuáles categorías están creciendo y cuáles decreciendo.',
    ],
    questions: [
      '¿En cuántas categorías de producto compite el mercado?',
      '¿Cuál es la categoría más grande y cuál tiene mayor crecimiento?',
      '¿Hay categorías emergentes que aún no están bien atendidas?',
      '¿Qué innovaciones de producto están transformando el mercado?',
    ],
    tip: 'El análisis de portafolio del mercado ayuda a identificar dónde hay espacio para crecer que la competencia no ha visto.',
    tasks: [
      { name: 'Mapear todas las categorías de producto del mercado con su tamaño', priority: 'high' },
      { name: 'Calcular el crecimiento de cada categoría (últimos 3 años)', priority: 'high' },
      { name: 'Identificar categorías emergentes o de alto potencial', priority: 'medium' },
    ],
  },

  {
    match: s => s.includes('canal') && !s.includes('comunicacion'),
    icon: '🚚', color: 'from-sky-600 to-sky-700',
    what: 'Analiza cómo llegan los productos al consumidor final: canales de distribución y su importancia.',
    goal: 'Un mapa de canales del mercado con sus pesos relativos y tendencias.',
    steps: [
      'Identifica todos los canales de distribución que existen en el mercado.',
      'Estima qué % de las ventas pasa por cada canal.',
      'Analiza cuáles canales están ganando y perdiendo participación.',
    ],
    questions: [
      '¿Por qué canales llega el producto al consumidor final en tu mercado?',
      '¿Qué canal es el más relevante hoy y cuál está creciendo más?',
      '¿Hay algún canal digital o directo que esté transformando la distribución?',
      '¿Cuál es el canal donde mejor estamos posicionados nosotros?',
    ],
    tip: 'Los cambios de canal son disruptores silenciosos. El e-commerce ha cambiado reglas de juego en casi todos los mercados de consumo masivo.',
    tasks: [
      { name: 'Identificar todos los canales de distribución del mercado', priority: 'high' },
      { name: 'Estimar participación de ventas por canal (%)', priority: 'high' },
      { name: 'Analizar tendencias: qué canales crecen y cuáles pierden peso', priority: 'medium' },
    ],
  },

  {
    match: s => s.includes('participacion') || s.includes('competidor'),
    icon: '⚔️', color: 'from-rose-600 to-rose-700',
    what: 'Mapea quiénes son los competidores y qué participación de mercado tiene cada uno.',
    goal: 'Un ranking de competidores con sus shares de mercado y tendencias.',
    steps: [
      'Lista todos los competidores relevantes del mercado.',
      'Estima la participación de mercado de cada uno (% de ventas).',
      'Identifica quiénes están ganando y perdiendo participación.',
    ],
    questions: [
      '¿Quiénes son los 5 principales competidores y qué share tiene cada uno?',
      '¿Quién ha ganado más mercado en los últimos 3 años y por qué?',
      '¿Hay competidores entrantes o internacionales que estén amenazando el mercado?',
      '¿Cuál es nuestra participación actual y cómo ha evolucionado?',
    ],
    tip: 'Los datos de share suelen estar en reportes de gremios o Nielsen. Si no tienes datos exactos, una estimación de mercado con fuentes secundarias es suficiente para empezar.',
    tasks: [
      { name: 'Identificar los 5-8 competidores principales del mercado', priority: 'high' },
      { name: 'Estimar participación de mercado de cada competidor (%)', priority: 'high' },
      { name: 'Analizar tendencia: quién gana y quién pierde participación', priority: 'medium' },
      { name: 'Incluir nuestra propia participación y tendencia', priority: 'medium' },
    ],
  },

  // ── Estrategia por Unidad de Negocio ────────────────────────────────────
  {
    match: s => s.includes('posicionamiento'),
    icon: '🏆', color: 'from-indigo-600 to-indigo-700',
    what: 'Define cómo quieres ser percibido en la mente del consumidor frente a la competencia.',
    goal: 'Una declaración de posicionamiento clara con los atributos diferenciadores.',
    steps: [
      'Identifica a quién te diriges (target específico) y qué necesidad cubres.',
      'Define cuál es tu principal diferenciador frente a la competencia.',
      'Redacta la declaración de posicionamiento: "Para [target], [marca] es la única [categoría] que [beneficio clave] porque [razón para creer]."',
    ],
    questions: [
      '¿En la mente de tu cliente ideal, qué eres tú vs la competencia?',
      '¿En qué atributo quieres ser el líder indiscutible?',
      '¿Cuál es tu "razón para creer" — la prueba de que puedes cumplir tu promesa?',
      '¿Tu posicionamiento actual es el que quieres, o es el que te dejaron los competidores?',
    ],
    tip: 'El posicionamiento no se declara — se construye con cada punto de contacto. Define uno que puedas sostener con acciones concretas.',
    tasks: [
      { name: 'Definir el target específico y la necesidad que resolvemos', priority: 'high' },
      { name: 'Identificar los 3 atributos de posicionamiento más importantes', priority: 'high' },
      { name: 'Redactar la declaración de posicionamiento', priority: 'high' },
      { name: 'Comparar nuestro posicionamiento con el de los 2 principales competidores', priority: 'medium' },
    ],
  },

  {
    match: s => s.includes('portafolio') && !s.includes('estrategia'),
    icon: '🗂️', color: 'from-orange-600 to-orange-700',
    what: 'Define la estrategia de portafolio de productos/servicios para esta unidad de negocio.',
    goal: 'Un portafolio priorizado con roles definidos para cada producto (estrella, vaca, apuesta).',
    steps: [
      'Lista todos los productos/SKUs actuales con sus ventas y margen.',
      'Clasifica cada uno según su participación de mercado y crecimiento.',
      'Define el rol estratégico de cada producto: crecer, mantener, optimizar o descontinuar.',
    ],
    questions: [
      '¿Cuántos productos/SKUs tienes y cuántos generan el 80% de las ventas?',
      '¿Hay productos que deberías eliminar porque drenan recursos sin retorno?',
      '¿Hay espacios en el mercado que deberías cubrir con nuevos productos?',
      '¿Cuál es tu producto estrella y qué inversión requiere para crecer?',
    ],
    tip: 'La mayoría de empresas tienen demasiados productos. Simplificar el portafolio suele mejorar la rentabilidad y el enfoque.',
    tasks: [
      { name: 'Listar todos los productos/servicios con ventas y margen del último año', priority: 'high' },
      { name: 'Clasificar cada producto: estrella, vaca, interrogante o perro', priority: 'high' },
      { name: 'Definir rol estratégico: crecer / mantener / optimizar / descontinuar', priority: 'medium' },
      { name: 'Identificar gaps de portafolio que la competencia está aprovechando', priority: 'medium' },
    ],
  },
]

/**
 * Find the best matching guide for a given section name.
 * Returns null if no match is found.
 */
export function findGuide(sectionName) {
  if (!sectionName) return null
  const norm = sectionName.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  return SECTION_GUIDES.find(g => g.match(norm)) ?? null
}
